// Public read-only Sanity client for the frontend (CDN-cached).
import { createClient } from "@sanity/client";
import imageUrlBuilder from "@sanity/image-url";

type SanityImageSource = Parameters<ReturnType<typeof imageUrlBuilder>["image"]>[0];

export const SANITY_PROJECT_ID = "dgr2n1ui";
export const SANITY_DATASET = "production";
export const SANITY_API_VERSION = "2024-01-01";

export const sanityClient = createClient({
  projectId: SANITY_PROJECT_ID,
  dataset: SANITY_DATASET,
  apiVersion: SANITY_API_VERSION,
  useCdn: true,
});

const builder = imageUrlBuilder(sanityClient);
export function urlFor(source: SanityImageSource) {
  return builder.image(source);
}

// ----- Types matching Studio schemas -----
export type BlogCategory =
  | "roofing-tips"
  | "solar-energy"
  | "roof-repairs"
  | "storm-damage"
  | "energy-efficiency"
  | "residential-roofing"
  | "commercial-roofing";

export interface SanityBlogPostListItem {
  _id: string;
  slug: string;
  category: BlogCategory;
  coverImageUrl: string;
  title_en: string;
  title_es: string;
  excerpt_en: string;
  excerpt_es: string;
  readMinutes: number;
  publishedAt: string;
}

export interface SanityBlogPostFull extends SanityBlogPostListItem {
  content_en: string;
  content_es: string;
}

const POST_LIST_PROJECTION = `{
  _id,
  "slug": slug.current,
  category,
  "coverImageUrl": coverImage.asset->url,
  title_en,
  title_es,
  excerpt_en,
  excerpt_es,
  readMinutes,
  publishedAt,
}`;

const POST_FULL_PROJECTION = `{
  _id,
  "slug": slug.current,
  category,
  "coverImageUrl": coverImage.asset->url,
  title_en,
  title_es,
  excerpt_en,
  excerpt_es,
  content_en,
  content_es,
  readMinutes,
  publishedAt,
}`;

export async function getPublishedPosts(): Promise<SanityBlogPostListItem[]> {
  return sanityClient.fetch(
    `*[_type == "blogPost" && published == true] | order(publishedAt desc) ${POST_LIST_PROJECTION}`,
  );
}

export async function getRecentPosts(limit = 3): Promise<SanityBlogPostListItem[]> {
  return sanityClient.fetch(
    `*[_type == "blogPost" && published == true] | order(publishedAt desc) [0...$limit] ${POST_LIST_PROJECTION}`,
    { limit },
  );
}

export async function getPostBySlug(
  slug: string,
): Promise<{ post: SanityBlogPostFull | null; related: SanityBlogPostListItem[] }> {
  const post = await sanityClient.fetch<SanityBlogPostFull | null>(
    `*[_type == "blogPost" && slug.current == $slug && published == true][0] ${POST_FULL_PROJECTION}`,
    { slug },
  );
  if (!post) return { post: null, related: [] };
  const related = await sanityClient.fetch<SanityBlogPostListItem[]>(
    `*[_type == "blogPost" && published == true && category == $category && slug.current != $slug] | order(publishedAt desc) [0...3] ${POST_LIST_PROJECTION}`,
    { category: post.category, slug },
  );
  return { post, related };
}

// ----- Gallery -----
export type GalleryCategory = "Roofing" | "Solar" | "Repairs" | "Commercial";

export interface SanityGalleryItem {
  _id: string;
  imageUrl: string;
  alt: string;
  category: GalleryCategory;
  order: number;
}

export async function getGalleryItems(): Promise<SanityGalleryItem[]> {
  return sanityClient.fetch(
    `*[_type == "galleryItem"] | order(order asc, _createdAt asc) {
      _id,
      "imageUrl": image.asset->url,
      alt,
      category,
      order
    }`,
  );
}

// ----- Before & After -----
export interface SanityBeforeAfterItem {
  _id: string;
  title_en: string;
  title_es: string;
  beforeUrl: string;
  afterUrl: string;
  order: number;
}

export async function getBeforeAfterItems(): Promise<SanityBeforeAfterItem[]> {
  return sanityClient.fetch(
    `*[_type == "beforeAfter"] | order(order asc, _createdAt asc) {
      _id,
      title_en,
      title_es,
      "beforeUrl": beforeImage.asset->url,
      "afterUrl": afterImage.asset->url,
      order
    }`,
  );
}

